import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Building2, Mail, Phone, MapPin, Globe, DollarSign, Tag, Calendar as CalendarIcon, User, Wallet, ChevronDown, ChevronUp, FileText } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Textarea } from '@/components/ui/textarea';
import { format } from 'date-fns';
import axios from 'axios';
import { WalletAddressManager, CryptoHoldingsManager, InvestmentFocusManager } from './CryptoProfileFields';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CreateOrganizationModal = ({ isOpen, onClose, onSubmit, editOrganization = null }) => {
  // Determine edit context: 'organization' for Organization Info, 'crypto' for Crypto & Investment Profile, 'preferences' for Preferences, null for create new
  const editContext = editOrganization?.editContext || null;
  const isEditingOrganizationInfo = editContext === 'organization';
  const isEditingCryptoInfo = editContext === 'crypto';
  const isEditingPreferences = editContext === 'preferences';
  
  const [formData, setFormData] = useState({
    name: '',
    type: 'lp',
    status: 'active',
    primaryContact: '',
    primaryContactRole: '',
    primaryContactEmail: '',
    primaryContactPhone: '',
    location: '',
    aum: '',
    website: '',
    tags: '',
    lastContact: null,
    // Crypto and profile fields
    walletAddresses: [],
    cryptoHoldings: {},
    connectionType: 'external',
    notes: '',
    preferencesNotes: '',
    socialLinkedin: '',
    socialTwitter: '',
    investmentFocus: []
  });

  const [showCryptoSection, setShowCryptoSection] = useState(false);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);

  // Fetch available organization tags
  useEffect(() => {
    if (isOpen) {
      fetchOrganizationTags();
    }
  }, [isOpen]);

  const fetchOrganizationTags = async () => {
    setIsLoadingTags(true);
    try {
      const response = await axios.get(`${API_URL}/api/tags?tag_type=organizations`);
      setAvailableTags(response.data);
    } catch (error) {
      console.error('Error fetching tags:', error);
    } finally {
      setIsLoadingTags(false);
    }
  };

  useEffect(() => {
    if (editOrganization) {
      setFormData({
        name: editOrganization.name || '',
        type: editOrganization.type || 'lp',
        status: editOrganization.status || 'active',
        primaryContact: editOrganization.primaryContact || '',
        primaryContactRole: editOrganization.primaryContactRole || '',
        primaryContactEmail: editOrganization.primaryContactEmail || '',
        primaryContactPhone: editOrganization.primaryContactPhone || '',
        location: editOrganization.location || '',
        aum: editOrganization.aum || '',
        website: editOrganization.website || '',
        tags: editOrganization.tags ? editOrganization.tags.map(t => t.name).join(', ') : '',
        lastContact: editOrganization.lastContact ? new Date(editOrganization.lastContact) : null,
        // Crypto and profile fields
        walletAddresses: editOrganization.walletAddresses || [],
        cryptoHoldings: editOrganization.cryptoHoldings || {},
        connectionType: editOrganization.connectionType || 'external',
        notes: editOrganization.notes || '',
        preferencesNotes: editOrganization.preferencesNotes || '',
        socialLinkedin: editOrganization.socialLinkedin || '',
        socialTwitter: editOrganization.socialTwitter || '',
        investmentFocus: editOrganization.investmentFocus || []
      });
      // Show crypto section if there's crypto data or if editing crypto info
      if (editContext === 'crypto' || editOrganization.walletAddresses?.length > 0 || Object.keys(editOrganization.cryptoHoldings || {}).length > 0) {
        setShowCryptoSection(true);
      }
    } else {
      resetForm();
    }
  }, [editOrganization, isOpen, editContext]);

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'lp',
      status: 'active',
      primaryContact: '',
      primaryContactRole: '',
      primaryContactEmail: '',
      primaryContactPhone: '',
      location: '',
      aum: '',
      website: '',
      tags: '',
      lastContact: null,
      // Crypto and profile fields
      walletAddresses: [],
      cryptoHoldings: {},
      connectionType: 'external',
      notes: '',
      preferencesNotes: '',
      socialLinkedin: '',
      socialTwitter: '',
      investmentFocus: []
    });
    setErrors({});
    setShowCryptoSection(false);
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Organization name is required';
    if (formData.primaryContactEmail && !/\S+@\S+\.\S+/.test(formData.primaryContactEmail)) {
      newErrors.primaryContactEmail = 'Invalid email format';
    }

    // Validate tags
    if (formData.tags.trim()) {
      const tagNames = formData.tags.split(',').map(t => t.trim()).filter(t => t);
      const invalidTags = tagNames.filter(tagName => 
        !availableTags.some(tag => tag.name.toLowerCase() === tagName.toLowerCase())
      );
      
      if (invalidTags.length > 0) {
        newErrors.tags = `Invalid tag(s): ${invalidTags.join(', ')}. Please use existing Organization Tags.`;
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setIsSubmitting(true);
    
    try {
      // Convert tag names to tag IDs
      const tagNames = formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(t => t) : [];
      const tagIds = tagNames.map(tagName => {
        const tag = availableTags.find(t => t.name.toLowerCase() === tagName.toLowerCase());
        return tag ? tag.id : null;
      }).filter(id => id !== null);

      const submitData = {
        name: formData.name.trim(),
        type: formData.type,
        status: formData.status,
        primaryContact: formData.primaryContact.trim() || null,
        primaryContactRole: formData.primaryContactRole.trim() || null,
        primaryContactEmail: formData.primaryContactEmail.trim() || null,
        primaryContactPhone: formData.primaryContactPhone.trim() || null,
        location: formData.location.trim() || null,
        aum: formData.aum.trim() || null,
        website: formData.website.trim() || null,
        tags: tagIds,
        lastContact: formData.lastContact ? format(formData.lastContact, 'yyyy-MM-dd') : null,
        // Crypto and profile fields
        walletAddresses: formData.walletAddresses,
        cryptoHoldings: formData.cryptoHoldings,
        connectionType: formData.connectionType,
        notes: formData.notes.trim() || null,
        preferencesNotes: formData.preferencesNotes.trim() || null,
        socialLinkedin: formData.socialLinkedin.trim() || null,
        socialTwitter: formData.socialTwitter.trim() || null,
        investmentFocus: formData.investmentFocus
      };

      await onSubmit(submitData, editOrganization?.id);
      resetForm();
      onClose();
    } catch (error) {
      console.error('Error submitting organization:', error);
      if (error.response?.data?.detail) {
        setErrors(prev => ({ ...prev, tags: error.response.data.detail }));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2 }}
          className="relative w-full max-w-4xl max-h-[95vh] bg-[#0f1115] border border-white/10 rounded-lg shadow-2xl flex flex-col"
        >
          {/* Header - Fixed */}
          <div className="p-4 border-b border-white/10 shrink-0">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">
                {editOrganization 
                  ? isEditingOrganizationInfo 
                    ? 'Edit Organization Info' 
                    : isEditingCryptoInfo 
                      ? 'Edit Crypto & Investment Profile'
                      : isEditingPreferences
                        ? 'Edit Preferences'
                        : 'Edit Organization'
                  : 'Add New Organization'}
              </h2>
              <button
                onClick={onClose}
                className="text-zinc-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Form - Scrollable */}
          <div className="overflow-y-auto flex-1">
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {/* Organization Information - Show when creating new or editing organization info */}
            {(!editOrganization || isEditingOrganizationInfo) && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                <Building2 size={16} />
                Organization Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="name" className="text-zinc-300 mb-1.5">
                    Organization Name <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className={`bg-[#0a0a0a] border-white/10 text-zinc-200 ${errors.name ? 'border-red-500' : ''}`}
                    placeholder="Sequoia Capital"
                  />
                  {errors.name && <p className="text-xs text-red-400 mt-1">{errors.name}</p>}
                </div>
                <div>
                  <Label htmlFor="type" className="text-zinc-300 mb-1.5">
                    Type
                  </Label>
                  <Select value={formData.type} onValueChange={(value) => handleChange('type', value)}>
                    <SelectTrigger className="bg-[#0a0a0a] border-white/10 text-zinc-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0f1115] border-white/10">
                      <SelectItem value="lp">LP</SelectItem>
                      <SelectItem value="gp">GP</SelectItem>
                      <SelectItem value="portfolio">Portfolio Company</SelectItem>
                      <SelectItem value="advisor">Advisor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label htmlFor="status" className="text-zinc-300 mb-1.5">
                    Status
                  </Label>
                  <Select value={formData.status} onValueChange={(value) => handleChange('status', value)}>
                    <SelectTrigger className="bg-[#0a0a0a] border-white/10 text-zinc-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0f1115] border-white/10">
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="prospect">Prospect</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="location" className="text-zinc-300 mb-1.5">
                    Location
                  </Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => handleChange('location', e.target.value)}
                    className="bg-[#0a0a0a] border-white/10 text-zinc-200"
                    placeholder="San Francisco, CA"
                  />
                </div>
                <div>
                  <Label htmlFor="aum" className="text-zinc-300 mb-1.5">
                    Deal Size (USD)
                  </Label>
                  <Input
                    id="aum"
                    value={formData.aum}
                    onChange={(e) => handleChange('aum', e.target.value)}
                    className="bg-[#0a0a0a] border-white/10 text-zinc-200"
                    placeholder="2530000"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="website" className="text-zinc-300 mb-1.5">
                  Website
                </Label>
                <Input
                  id="website"
                  value={formData.website}
                  onChange={(e) => handleChange('website', e.target.value)}
                  className="bg-[#0a0a0a] border-white/10 text-zinc-200"
                  placeholder="sequoiacap.com"
                />
              </div>
            </div>
            )}

            {/* Primary Contact Information - Show when creating new or editing organization info */}
            {(!editOrganization || isEditingOrganizationInfo) && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                <User size={16} />
                Primary Contact
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="primaryContact" className="text-zinc-300 mb-1.5">
                    Contact Name
                  </Label>
                  <Input
                    id="primaryContact"
                    value={formData.primaryContact}
                    onChange={(e) => handleChange('primaryContact', e.target.value)}
                    className="bg-[#0a0a0a] border-white/10 text-zinc-200"
                    placeholder="Michael Chen"
                  />
                </div>
                <div>
                  <Label htmlFor="primaryContactRole" className="text-zinc-300 mb-1.5">
                    Role / Title
                  </Label>
                  <Input
                    id="primaryContactRole"
                    value={formData.primaryContactRole}
                    onChange={(e) => handleChange('primaryContactRole', e.target.value)}
                    className="bg-[#0a0a0a] border-white/10 text-zinc-200"
                    placeholder="Partner"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="primaryContactEmail" className="text-zinc-300 mb-1.5">
                    Email
                  </Label>
                  <Input
                    id="primaryContactEmail"
                    type="email"
                    value={formData.primaryContactEmail}
                    onChange={(e) => handleChange('primaryContactEmail', e.target.value)}
                    className={`bg-[#0a0a0a] border-white/10 text-zinc-200 ${errors.primaryContactEmail ? 'border-red-500' : ''}`}
                    placeholder="m.chen@sequoiacap.com"
                  />
                  {errors.primaryContactEmail && <p className="text-xs text-red-400 mt-1">{errors.primaryContactEmail}</p>}
                </div>
                <div>
                  <Label htmlFor="primaryContactPhone" className="text-zinc-300 mb-1.5">
                    Phone
                  </Label>
                  <Input
                    id="primaryContactPhone"
                    value={formData.primaryContactPhone}
                    onChange={(e) => handleChange('primaryContactPhone', e.target.value)}
                    className="bg-[#0a0a0a] border-white/10 text-zinc-200"
                    placeholder="+1 (650) 555-0101"
                  />
                </div>
              </div>
            </div>
            )}

            {/* Additional Information - Show when creating new or editing organization info */}
            {(!editOrganization || isEditingOrganizationInfo) && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                <Tag size={16} />
                Additional Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="tags" className="text-zinc-300 mb-1.5">
                    Tags (comma-separated)
                  </Label>
                  <Input
                    id="tags"
                    value={formData.tags}
                    onChange={(e) => handleChange('tags', e.target.value)}
                    className={`bg-[#0a0a0a] border-white/10 text-zinc-200 ${errors.tags ? 'border-red-500' : ''}`}
                    placeholder="Tier 1, Tech Focus"
                  />
                  {errors.tags && <p className="text-xs text-red-400 mt-1">{errors.tags}</p>}
                  {!isLoadingTags && availableTags.length > 0 && (
                    <div className="mt-2 text-xs text-zinc-500">
                      <span className="font-medium">Available:</span>{' '}
                      {availableTags.map((tag, idx) => (
                        <span key={tag.id}>
                          {tag.name}
                          {idx < availableTags.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <Label htmlFor="lastContact" className="text-zinc-300 mb-1.5">
                    Last Contact Date
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal bg-[#0a0a0a] border-white/10 text-zinc-200 hover:bg-white/5"
                      >
                        <CalendarIcon size={16} className="mr-2" />
                        {formData.lastContact ? format(formData.lastContact, 'PPP') : <span className="text-zinc-500">Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#0f1115] border-white/10">
                      <Calendar
                        mode="single"
                        selected={formData.lastContact}
                        onSelect={(date) => handleChange('lastContact', date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
            )}

            {/* Crypto & Profile Section - Show when creating new or editing crypto info */}
            {(!editOrganization || isEditingCryptoInfo) && (
            <div className="space-y-3 border-t border-white/10 pt-4">
              <button
                type="button"
                onClick={() => setShowCryptoSection(!showCryptoSection)}
                className="w-full flex items-center justify-between text-sm font-medium text-zinc-300 hover:text-white transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Wallet size={16} />
                  Crypto & Investment Profile
                </div>
                {showCryptoSection ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>

              {showCryptoSection && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-4 pt-3"
                >
                  {/* Connection Type & Social Links */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <Label htmlFor="connectionType" className="text-zinc-300 mb-1.5">
                        Connection Type
                      </Label>
                      <Select
                        value={formData.connectionType}
                        onValueChange={(value) => handleChange('connectionType', value)}
                      >
                        <SelectTrigger className="bg-[#0a0a0a] border-white/10 text-zinc-200">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="external">External</SelectItem>
                          <SelectItem value="internal">Internal</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="socialLinkedin" className="text-zinc-300 mb-1.5">
                        LinkedIn
                      </Label>
                      <Input
                        id="socialLinkedin"
                        value={formData.socialLinkedin}
                        onChange={(e) => handleChange('socialLinkedin', e.target.value)}
                        className="bg-[#0a0a0a] border-white/10 text-zinc-200"
                        placeholder="linkedin.com/company/..."
                      />
                    </div>
                    <div>
                      <Label htmlFor="socialTwitter" className="text-zinc-300 mb-1.5">
                        Twitter/X
                      </Label>
                      <Input
                        id="socialTwitter"
                        value={formData.socialTwitter}
                        onChange={(e) => handleChange('socialTwitter', e.target.value)}
                        className="bg-[#0a0a0a] border-white/10 text-zinc-200"
                        placeholder="@company"
                      />
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <Label htmlFor="notes" className="text-zinc-300 mb-1.5">
                      Notes
                    </Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => handleChange('notes', e.target.value)}
                      className="bg-[#0a0a0a] border-white/10 text-zinc-200 min-h-[80px]"
                      placeholder="General notes about this organization..."
                    />
                  </div>

                  {/* Investment Focus */}
                  <div className="text-zinc-200">
                    <InvestmentFocusManager
                      investmentFocus={formData.investmentFocus}
                      onChange={(value) => handleChange('investmentFocus', value)}
                    />
                  </div>

                  {/* Wallet Addresses */}
                  <div className="text-zinc-200">
                    <WalletAddressManager
                      walletAddresses={formData.walletAddresses}
                      onChange={(value) => handleChange('walletAddresses', value)}
                    />
                  </div>

                  {/* Crypto Holdings */}
                  <div className="text-zinc-200">
                    <CryptoHoldingsManager
                      cryptoHoldings={formData.cryptoHoldings}
                      onChange={(value) => handleChange('cryptoHoldings', value)}
                    />
                  </div>
                </motion.div>
              )}
            </div>
            )}

            {/* Preferences Section - Show when creating new or editing preferences */}
            {(!editOrganization || isEditingPreferences) && (
            <div className="space-y-3 border-t border-white/10 pt-4">
              <h3 className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                <FileText size={16} />
                Preferences & Notes
              </h3>
              <div>
                <Label htmlFor="preferencesNotes" className="text-zinc-300 mb-1.5">
                  Notes
                </Label>
                <Textarea
                  id="preferencesNotes"
                  value={formData.preferencesNotes}
                  onChange={(e) => handleChange('preferencesNotes', e.target.value)}
                  className="bg-[#0a0a0a] border-white/10 text-zinc-200 min-h-[200px] resize-y"
                  placeholder="Add any notes, preferences, or important information about this organization..."
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Use this space to track communication preferences, important dates, or any other relevant information.
                </p>
              </div>
            </div>
            )}

            {/* Form Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
              <Button
                type="button"
                variant="ghost"
                onClick={onClose}
                className="text-zinc-400 hover:text-white"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {isSubmitting ? 'Saving...' : (editOrganization ? 'Update Organization' : 'Create Organization')}
              </Button>
            </div>
          </form>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default CreateOrganizationModal;